import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { collection, doc } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Slider from "@mui/material/Slider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MuiColorInput } from "mui-color-input";
import { ExternalLink } from "lucide-react";
import { DEFAULT_EVENT_CONFIG } from "@event-planner/shared";
import type { Event, EventConfig, SessionTypeConfig } from "@event-planner/shared";
import { db } from "../../lib/firebase";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { SchemaField, type FieldConfig } from "../../components/forms/SchemaField";
import { ImageUpload } from "../../components/uploads/ImageUpload";
import { EventCardPreview } from "../../components/previews/EventCardPreview";
import { FaqManager } from "./FaqManager";
import { useCreateEvent } from "../../hooks/useEventMutations";
import { useUpdateEvent } from "../../hooks/useEventMutations";
import { updateEvent, type UpdateEventInput } from "../../services/eventService";
import {
  LocationPicker,
  isPickerValueDirty,
  pickerValueStub,
  type LocationPickerValue,
} from "../../components/forms/LocationPicker";
import { useLocationSaveConfirm } from "../../components/forms/useLocationSaveConfirm";
import { coordinatesToTimezone } from "../../utils/coordinatesToTimezone";

const DEFAULT_VENUE_COLOR = "#1565C0";
import {
  uploadToStorage,
  generateStorageFilename,
} from "../../services/storageService";
import { computeBlurhashFromBlob } from "../../utils/blurhash";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useAuth } from "../../context/AuthContext";
import { eventSchema, type EventFormData } from "../../schemas/eventSchema";
import { PhoneSaveBar } from "../../components/phone/PhoneSaveBar";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  isSharePointConfigured,
  getEventPageUrl,
} from "../../utils/sharepoint";

// Curated IANA timezone list covering major regions
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  // North America
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska Time (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (Honolulu)" },
  { value: "America/Toronto", label: "Eastern Time (Toronto)" },
  { value: "America/Vancouver", label: "Pacific Time (Vancouver)" },
  { value: "America/Mexico_City", label: "Central Time (Mexico City)" },
  // Europe
  { value: "Europe/London", label: "UK Time (London)" },
  { value: "Europe/Paris", label: "Central European Time (Paris)" },
  { value: "Europe/Berlin", label: "Central European Time (Berlin)" },
  { value: "Europe/Madrid", label: "Central European Time (Madrid)" },
  { value: "Europe/Rome", label: "Central European Time (Rome)" },
  { value: "Europe/Amsterdam", label: "Central European Time (Amsterdam)" },
  { value: "Europe/Zurich", label: "Central European Time (Zurich)" },
  { value: "Europe/Moscow", label: "Moscow Time" },
  { value: "Europe/Istanbul", label: "Turkey Time (Istanbul)" },
  // Middle East / Africa
  { value: "Asia/Dubai", label: "Gulf Time (Dubai)" },
  { value: "Asia/Riyadh", label: "Arabia Time (Riyadh)" },
  { value: "Africa/Johannesburg", label: "South Africa Time (Johannesburg)" },
  { value: "Africa/Cairo", label: "Eastern European Time (Cairo)" },
  // Asia Pacific
  { value: "Asia/Kolkata", label: "India Time (Kolkata)" },
  { value: "Asia/Singapore", label: "Singapore Time" },
  { value: "Asia/Shanghai", label: "China Time (Shanghai)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong Time" },
  { value: "Asia/Tokyo", label: "Japan Time (Tokyo)" },
  { value: "Asia/Seoul", label: "Korea Time (Seoul)" },
  // Oceania
  { value: "Australia/Sydney", label: "Eastern Australia (Sydney)" },
  { value: "Australia/Melbourne", label: "Eastern Australia (Melbourne)" },
  { value: "Australia/Perth", label: "Western Australia (Perth)" },
  { value: "Pacific/Auckland", label: "New Zealand Time (Auckland)" },
];

function systemAudienceId(eventId: string, slug: string): string {
  return `${eventId}_${slug}`;
}

/**
 * Per-category default audience for a brand-new event. ADR-120 collapsed
 * the prior multi-default array to a single audience reference, so
 * categories that previously defaulted to "All Invitees + All
 * Companions" now default to "All Members" (the closest single role
 * audience). Organizers can pick a custom-group default after the
 * event is created and groups have been authored.
 */
function defaultAudienceForCategory(
  eventId: string,
  categoryId: string,
): string | null {
  switch (categoryId) {
    case "presentation":
    case "meal":
    case "activity":
      return systemAudienceId(eventId, "all-members");
    case "breakout":
      return systemAudienceId(eventId, "all-internal");
    default:
      return null;
  }
}

function buildDefaultEventConfig(eventId: string | null): EventConfig {
  return {
    ...DEFAULT_EVENT_CONFIG,
    sessionTypes: DEFAULT_EVENT_CONFIG.sessionTypes.map(
      (category): SessionTypeConfig => ({
        ...category,
        defaultAudienceId: eventId
          ? defaultAudienceForCategory(eventId, category.id)
          : null,
      }),
    ),
  };
}

function buildDefaultValues(
  initialData: Event | undefined,
  eventId: string | null,
): EventFormData {
  if (initialData) {
    return {
      name: initialData.name,
      description: initialData.description,
      venueLocationId: initialData.venueLocationId ?? null,
      startDate: initialData.startDate,
      endDate: initialData.endDate,
      timezone: initialData.timezone,
      status: initialData.status,
      heroImageURL: initialData.heroImageURL,
      heroBlurhash: initialData.heroBlurhash,
      heroTitleOverride: initialData.heroTitleOverride,
      heroOverlayColor: initialData.heroOverlayColor,
      heroOverlayOpacity: initialData.heroOverlayOpacity,
      heroOverlayDirection: initialData.heroOverlayDirection,
      config: initialData.config,
    };
  }

  return {
    name: "",
    description: null,
    venueLocationId: null,
    startDate: "",
    endDate: "",
    timezone: "",
    status: "draft",
    heroImageURL: null,
    heroBlurhash: null,
    heroTitleOverride: null,
    heroOverlayColor: "#050024",
    heroOverlayOpacity: 0.7,
    heroOverlayDirection: "to bottom",
    config: buildDefaultEventConfig(eventId),
  };
}

function buildEventFormUpdateInput(
  data: EventFormData,
  venueLocationId: string | null,
): UpdateEventInput {
  const { config, ...topLevel } = data;
  return {
    ...topLevel,
    venueLocationId,
    "config.agendaLabel": config.agendaLabel,
    "config.myScheduleLabel": config.myScheduleLabel,
    "config.speakersLabel": config.speakersLabel,
    "config.sessionsLabel": config.sessionsLabel,
    "config.faqs": config.faqs,
  };
}

const BASIC_INFO_FIELDS: FieldConfig<EventFormData>[] = [
  { name: "name", label: "Event Name", type: "text", required: true },
  { name: "description", label: "Description", type: "textarea", rows: 3 },
  {
    name: "status",
    label: "Status",
    type: "select",
    required: true,
    options: [
      { value: "draft", label: "Draft" },
      { value: "published", label: "Published" },
      { value: "archived", label: "Archived" },
    ],
  },
];

const DEFAULT_HERO_IMAGES = [
  "/event-hero-1-city.png",
  "/event-hero-2-table.png",
  "/event-hero-3-mountain.png",
  "/event-hero-4-mountain.png",
  "/event-hero-5-mountain.png",
  "/event-hero-6-beach.png",
];

const DATE_TIME_FIELDS: FieldConfig<EventFormData>[] = [
  { name: "startDate", label: "Start Date", type: "date", required: true },
  { name: "endDate", label: "End Date", type: "date", required: true },
  {
    name: "timezone",
    label: "Timezone",
    type: "select",
    required: true,
    options: TIMEZONE_OPTIONS,
  },
];

type EventFormTab = "basic" | "location" | "faq";

interface EventFormProps {
  mode: "create" | "edit";
  initialData?: Event;
  /** Called after a successful update in edit mode (e.g. to refetch the parent). */
  onSaved?: () => void;
}

export function EventForm({ mode, initialData, onSaved }: EventFormProps) {
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateEvent();
  const { update, loading: updating } = useUpdateEvent();
  const [serverError, setServerError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EventFormTab>("basic");

  // Pre-generate a stable Firestore ID for new events so the hero image
  // can be uploaded to the correct storage path before the event is saved.
  const pendingEventId = useMemo(
    () => (mode === "create" ? doc(collection(db, "events")).id : null),
    [mode],
  );
  const eventId = initialData?.id ?? pendingEventId!;

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: buildDefaultValues(initialData, pendingEventId),
  });

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting,
  );

  // LocationPicker value lives outside the form schema — it is resolved
  // at submit time into a concrete venueLocationId via
  // resolveLocationPickerValue, which may create a new eventLocations
  // document if the organizer entered a new place.
  const [pickerValue, setPickerValue] = useState<LocationPickerValue>(
    initialData?.venueLocationId
      ? pickerValueStub(initialData.venueLocationId, DEFAULT_VENUE_COLOR)
      : null,
  );

  // FORM-LEVEL DIRTY GATE for the LocationPicker.
  //
  // The picker has no built-in "dirty since hydrate" signal — the parent
  // form has to provide one, otherwise save always runs the picker
  // value through resolveLocationPickerValue, which writes to the
  // linked eventLocation doc on every save (including the case where
  // the user opened the form, never touched the Location tab, and just
  // wanted to update an FAQ).
  //
  // baselineRef holds the picker value at form-init time. Once the
  // stub gets hydrated by the picker's internal effect (label fills
  // in), we bump baseline to the hydrated value. Any divergence after
  // that is a real user edit. The dirty gate sits in onSubmit and
  // short-circuits when not dirty: preserve the original
  // venueLocationId verbatim, no resolver, no dialog, no doc write.
  const baselineRef = useRef<LocationPickerValue>(
    initialData?.venueLocationId
      ? pickerValueStub(initialData.venueLocationId, DEFAULT_VENUE_COLOR)
      : null,
  );
  useEffect(() => {
    const baseline = baselineRef.current;
    if (
      baseline?.locationId &&
      baseline.locationId === pickerValue?.locationId &&
      !baseline.label &&
      pickerValue?.label
    ) {
      baselineRef.current = pickerValue;
    }
  }, [pickerValue]);

  // Auto-derive event.timezone from the venue's coordinates whenever the
  // organizer picks or moves the venue pin. Keeps the tz field in sync
  // with where the event physically is. Organizer can still manually
  // override via the dropdown afterwards; the next venue change will
  // re-sync.
  const pickerCoordKey = pickerValue?.coordinates
    ? `${pickerValue.coordinates.lat},${pickerValue.coordinates.lng}`
    : "";
  useEffect(() => {
    if (!pickerValue?.coordinates) return;
    const derivedTz = coordinatesToTimezone(pickerValue.coordinates);
    if (!derivedTz) return;
    if (form.getValues("timezone") !== derivedTz) {
      form.setValue("timezone", derivedTz, { shouldDirty: true });
    }
  }, [pickerCoordKey, form, pickerValue?.coordinates]);

  const watchedName = form.watch("name");
  const watchedStartDate = form.watch("startDate");
  const watchedEndDate = form.watch("endDate");
  const watchedHeroImage = form.watch("heroImageURL");
  const watchedHeroTitle = form.watch("heroTitleOverride");
  const watchedOverlayColor = form.watch("heroOverlayColor");
  const watchedOverlayOpacity = form.watch("heroOverlayOpacity");
  const watchedOverlayDirection = form.watch("heroOverlayDirection");
  const submitting = creating || updating;

  // Resolve the venue's display strings from the picker for the
  // EventCardPreview. With the flat picker shape, label and city live
  // directly on the picker value regardless of whether it points at an
  // existing doc or a new authoring session — pickerValueStub kicks
  // off internal hydration on first render so saved data fills in.
  const venueLabelForPreview = pickerValue?.label ?? "";
  const venueCityForPreview = pickerValue?.city ?? "";

  /**
   * If heroImageURL is a local default path (e.g. "/event-hero-1-city.png"),
   * fetch and upload it to Storage, then update the event doc with the real URL.
   * Called after event creation when the membership exists and Storage rules pass.
   *
   * Also recomputes the BlurHash from the same blob — the form may
   * already have one populated from the default-image picker click,
   * but recomputing here keeps the field guaranteed in sync with
   * whichever bytes actually landed in Storage.
   */
  async function uploadDefaultHeroIfNeeded(id: string, heroURL: string | null) {
    if (!heroURL || !DEFAULT_HERO_IMAGES.includes(heroURL)) return;
    const response = await fetch(heroURL);
    const blob = await response.blob();
    const ext = heroURL.split(".").pop() ?? "png";
    const filename = generateStorageFilename(ext);
    const storagePath = `events/${id}/hero/${filename}`;
    const storageURL = await uploadToStorage(storagePath, blob, `image/${ext}`);
    const heroBlurhash = await computeBlurhashFromBlob(blob);
    await updateEvent(id, { heroImageURL: storageURL, heroBlurhash });
  }

  const { confirmAndResolve, dialog: locationSaveConfirmDialog } =
    useLocationSaveConfirm();

  async function onSubmit(data: EventFormData) {
    setServerError(null);
    try {
      if (mode === "create") {
        // Order matters here: the eventLocations create rule requires
        // `isEventOrganizerOrAdmin(eventId)`. For an organizer that only
        // passes once their participation row exists for the event, and
        // that row is written by inviteToEvent inside `create()` below
        // (admins pass on the claim alone, ADR-191, and are not seated).
        // So we must create the event FIRST (no venueLocationId), then
        // resolve the picker (which may write a new eventLocations doc),
        // and finally update the event with the resolved venueLocationId.
        // Reversing this order — even if the picker is in fresh-value
        // mode and skips the confirm dialog — fails on the rule.
        const id = await create(
          { ...data, venueLocationId: null, createdByUserId: user!.uid },
          pendingEventId!,
          user!.email ?? undefined,
        );
        const venueLocationId = await confirmAndResolve(pickerValue, id);
        if (venueLocationId === undefined) {
          // Picker confirm cancelled — event is already persisted with
          // no venue. Surface a partial-success snackbar so the
          // organizer knows the event exists and can re-pick a venue
          // from the edit page.
          showSuccess("Event created (venue not set)");
          navigate(`/events/${id}`, { replace: true });
          return;
        }
        if (venueLocationId !== null) {
          await update(id, { venueLocationId });
        }
        // Membership now exists — upload default hero image to Storage
        await uploadDefaultHeroIfNeeded(id, data.heroImageURL);
        showSuccess("Event created");
        navigate(`/events/${id}`, { replace: true });
      } else {
        // Form-level dirty gate (see baselineRef block above): if the
        // picker is structurally identical to its post-hydration
        // baseline, preserve initialData.venueLocationId verbatim and
        // skip confirmAndResolve. Without this gate, save would always
        // write to the linked eventLocation doc even when the user
        // never opened the Location tab.
        let venueLocationId: string | null;
        if (!isPickerValueDirty(baselineRef.current, pickerValue)) {
          venueLocationId = initialData?.venueLocationId ?? null;
        } else {
          // Resolve the picker value to a concrete locationId. If the
          // picker is in Editing mode (locationId set), the hook
          // prompts the organizer first to confirm shared-edit
          // semantics or explicitly fork. Returns undefined on cancel.
          const resolved = await confirmAndResolve(pickerValue, eventId);
          if (resolved === undefined) return;
          venueLocationId = resolved;
        }
        const dataWithVenueLocation = buildEventFormUpdateInput(
          data,
          venueLocationId,
        );
        // In edit mode, default images are already uploaded — if the organizer
        // picks a new default, upload it before saving and recompute the
        // BlurHash against the same blob so the persisted hash matches
        // whatever bytes actually live in Storage.
        if (data.heroImageURL && DEFAULT_HERO_IMAGES.includes(data.heroImageURL)) {
          const response = await fetch(data.heroImageURL);
          const blob = await response.blob();
          const ext = data.heroImageURL.split(".").pop() ?? "png";
          const filename = generateStorageFilename(ext);
          const storagePath = `events/${initialData!.id}/hero/${filename}`;
          dataWithVenueLocation.heroImageURL = await uploadToStorage(storagePath, blob, `image/${ext}`);
          dataWithVenueLocation.heroBlurhash = await computeBlurhashFromBlob(blob);
        }
        await update(initialData!.id, dataWithVenueLocation);
        showSuccess("Event updated");
        onSaved?.();
        navigate(`/events/${initialData!.id}`);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save event";
      setServerError(message);
      showError(message);
    }
  }

  const sharePointAction =
    mode === "edit" && initialData?.id && isSharePointConfigured() ? (
      <Button
        variant="outlined"
        startIcon={<ExternalLink size={18} />}
        href={getEventPageUrl(initialData.id)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-sharepoint-event-page-button"
      >
        Open in SharePoint
      </Button>
    ) : undefined;

  return (
    <FormProvider {...form}>
      {locationSaveConfirmDialog}
      {unsavedGuardDialog}
      <FormPageLayout
        title={mode === "create" ? "Create Event" : "Edit Event"}
        headerAction={sharePointAction}
        onSubmit={form.handleSubmit(onSubmit)}
        error={serverError}
        submitting={submitting}
        submitLabel={mode === "create" ? "Create Event" : "Save Changes"}
        testIdSlug="event"
        hideBottomActions
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v as EventFormTab)}
            sx={{ flex: 1 }}
          >
            <Tab label="Basic Info" value="basic" />
            <Tab label="Location" value="location" />
            <Tab label="FAQ" value="faq" />
          </Tabs>
          {/* Phone: submit moves to the sticky bar at the end of the form. */}
          {!isPhone && (
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : undefined}
            sx={{ ml: 2, flexShrink: 0 }}
            data-testid="event-form-submit"
          >
            {submitting ? "Saving..." : mode === "create" ? "Create Event" : "Save Changes"}
          </Button>
          )}
        </Box>

        {activeTab === "basic" && (
          <>
            <FormSection title="Details">
              {BASIC_INFO_FIELDS.map((field) => (
                <SchemaField key={field.name} config={field} control={form.control} />
              ))}
            </FormSection>

            <FormSection title="Dates & Time">
              {DATE_TIME_FIELDS.map((field) => (
                <SchemaField key={field.name} config={field} control={form.control} />
              ))}
            </FormSection>

            <FormSection title="Branding">
              <Controller
                control={form.control}
                name="heroTitleOverride"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    label="Hero Title"
                    placeholder={watchedName || "Event Name"}
                    helperText="Leave blank to use the event name"
                    fullWidth
                  />
                )}
              />

              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "flex-start" }}>
                {/* Left: image picker */}
                <Box sx={{ flex: 2, minWidth: 240 }}>
                  <Controller
                    control={form.control}
                    name="heroImageURL"
                    render={({ field }) => (
                      <ImageUpload
                        value={field.value}
                        onChange={field.onChange}
                        preset="hero"
                        storagePathPrefix={`events/${eventId}/hero`}
                        label="Hero / banner image"
                        defaultImages={DEFAULT_HERO_IMAGES}
                        onBlurhashChange={(hash) =>
                          form.setValue("heroBlurhash", hash, {
                            shouldDirty: true,
                          })
                        }
                      />
                    )}
                  />
                </Box>

                {/* Middle: scrim controls */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary">
                    Overlay Scrim
                  </Typography>
                  <Controller
                    control={form.control}
                    name="heroOverlayColor"
                    render={({ field }) => (
                      <MuiColorInput
                        value={field.value ?? "#050024"}
                        onChange={(val) => field.onChange(val || null)}
                        label="Color"
                        format="hex"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    control={form.control}
                    name="heroOverlayDirection"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? "to bottom"}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        label="Direction"
                        select
                        fullWidth
                      >
                        <MenuItem value="to top">Bottom to Top</MenuItem>
                        <MenuItem value="to bottom">Top to Bottom</MenuItem>
                        <MenuItem value="to left">Right to Left</MenuItem>
                        <MenuItem value="to right">Left to Right</MenuItem>
                      </TextField>
                    )}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Opacity: {Math.round((watchedOverlayOpacity ?? 0.7) * 100)}%
                    </Typography>
                    <Controller
                      control={form.control}
                      name="heroOverlayOpacity"
                      render={({ field }) => (
                        <Slider
                          value={field.value ?? 0.7}
                          onChange={(_, val) => field.onChange(val as number)}
                          min={0}
                          max={1}
                          step={0.05}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* Right: live preview */}
                <EventCardPreview
                  name={watchedHeroTitle || watchedName}
                  startDate={watchedStartDate}
                  endDate={watchedEndDate}
                  venue={venueLabelForPreview}
                  city={venueCityForPreview}
                  heroImageUrl={watchedHeroImage ?? null}
                  overlayColor={watchedOverlayColor ?? undefined}
                  overlayOpacity={watchedOverlayOpacity ?? undefined}
                  overlayDirection={watchedOverlayDirection ?? undefined}
                />
              </Box>
            </FormSection>
          </>
        )}

        {activeTab === "location" && (
          <>
            <FormSection title="Venue Location">
              <LocationPicker
                eventId={eventId}
                value={pickerValue}
                onChange={setPickerValue}
                defaultColor={DEFAULT_VENUE_COLOR}
                testIdSuffix="event"
                mapHeight={240}
              />
            </FormSection>
          </>
        )}

        {activeTab === "faq" && (
          <FormSection title="Frequently Asked Questions">
            <FaqManager />
          </FormSection>
        )}
        {isPhone && (
          <PhoneSaveBar>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} /> : undefined}
              data-testid="event-form-submit"
            >
              {submitting ? "Saving..." : mode === "create" ? "Create Event" : "Save Changes"}
            </Button>
          </PhoneSaveBar>
        )}
      </FormPageLayout>
    </FormProvider>
  );
}
